import React from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import PageMeta from '../../../components/common/PageMeta';
import { useAuth } from '../../../context/AuthContext';
import { useNavVisibility } from '../../../layout/useNavVisibility';
import { Page, Skeleton, StatusLabel, Surface, Tabs } from '../../../ui/v2';
import { ADDITIONAL_FAQS_HREF } from '../../KnowledgeQuestions/knowledgeModel';
import TeamSection from '../team/TeamSection';
import AdditionalFaqsSection from './AdditionalFaqsSection';
import EventsSection from './EventsSection';
import HoursSection from './HoursSection';
import KnowledgeBaseSection from './KnowledgeBaseSection';
import NotificationsSection from './NotificationsSection';
import OverviewSection from './OverviewSection';
import ReservationsSection from './ReservationsSection';
import VoiceSection from './VoiceSection';
import { useStoreInfoQuery } from './data';
import {
  LEGACY_TAB_TO_SECTION,
  SECTIONS,
  type Section,
  busyStatus,
  restaurantName,
} from './model';

/**
 * Store Info — page shell.
 *
 * `/store-info/{overview|voice|hours|events|reservations|notifications|knowledge-base|team}`.
 * Knowledge Base is listed only while Knowledge questions are on; its full
 * Additional FAQs list opens under it at `knowledge-base/faqs` (the old
 * `additional-faqs` route redirects there). Once the flag loads off, both
 * redirect to Overview.
 * Legacy `?tab=` links resolve to the matching section. Team is Owner-only
 * (platform admins included); everyone else is redirected to Overview.
 *
 * Layout: the title sits on the canvas. One surface holds the store
 * identity strip, the section tabs, and the active section. Each section
 * owns its queries, its content width, and its SaveBar.
 */
export const STORE_INFO_BASE = '/store-info';

export function canManageTeam(
  user: { role?: string; is_platform_admin?: boolean } | null | undefined,
): boolean {
  return user?.role === 'Owner' || !!user?.is_platform_admin;
}

const StoreInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const showTeam = canManageTeam(user);
  const { showKnowledgeQuestions, flagsPending } = useNavVisibility();
  const roleSections = SECTIONS.filter((s) => !s.ownerOnly || showTeam);
  const current = (roleSections.find((s) =>
    pathname.startsWith(`${STORE_INFO_BASE}/${s.key}`),
  )?.key ?? 'overview') as Section;
  // Until the flag loads, keep the open knowledge section's tab so it stays selected.
  const sections = roleSections.filter(
    (s) =>
      !s.knowledgeQuestionsOnly ||
      showKnowledgeQuestions ||
      (flagsPending && s.key === current),
  );
  const knowledgeRoute = (element: React.ReactElement) =>
    showKnowledgeQuestions || flagsPending ? (
      element
    ) : (
      <Navigate to={`${STORE_INFO_BASE}/overview`} replace />
    );

  const info = useStoreInfoQuery();
  const store = info.data;
  const status = busyStatus(store?.busy_mode_status);

  return (
    <>
      <PageMeta
        title="AskArthur | Store info"
        description="Store information"
      />
      <Page
        fill
        title="Store info"
        actions={
          <>
            {status && (
              <StatusLabel size="sm" tone={status.tone}>
                {status.label}
              </StatusLabel>
            )}
            {store && (
              <StatusLabel
                size="sm"
                tone={store.takes_reservations ? 'accent' : 'neutral'}
                hollow={!store.takes_reservations}
              >
                {store.takes_reservations
                  ? 'Reservations on'
                  : 'Reservations off'}
              </StatusLabel>
            )}
          </>
        }
      >
        <Surface className="flex min-h-0 flex-1 flex-col">
          <div
            className="flex items-center gap-x-3 overflow-hidden border-b border-v2-line px-4 py-3 text-v2-12 text-v2-fg-3 lg:px-6"
            aria-label="Store identity"
          >
            {info.status === 'pending' ? (
              <Skeleton width={180} />
            ) : store ? (
              <span
                className="min-w-0 truncate text-v2-15 font-semibold tracking-[-0.011em] text-v2-fg"
                title={restaurantName(store)}
              >
                {restaurantName(store)}
              </span>
            ) : (
              <span className="text-v2-fg-4">Store details unavailable</span>
            )}
          </div>
          {/* `-mb-px` lays the tab hairline over the header's own bottom border. */}
          <div className="-mb-px overflow-x-auto px-2 [scrollbar-width:none] lg:px-4 [&::-webkit-scrollbar]:hidden">
            <Tabs
              aria-label="Store info sections"
              hue="accent"
              value={current}
              onChange={(k) => navigate(`${STORE_INFO_BASE}/${k}`)}
              items={sections.map((s) => ({
                key: s.key,
                label: <span title={s.description}>{s.label}</span>,
              }))}
              className="w-max min-w-full"
            />
          </div>
          <div className="v2-scroll relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
            <Routes>
              <Route index element={<IndexRedirect />} />
              <Route path="overview" element={<OverviewSection />} />
              <Route path="voice" element={<VoiceSection />} />
              <Route path="hours" element={<HoursSection />} />
              <Route path="events" element={<EventsSection />} />
              <Route path="reservations" element={<ReservationsSection />} />
              <Route path="notifications" element={<NotificationsSection />} />
              <Route
                path="knowledge-base"
                element={knowledgeRoute(<KnowledgeBaseSection />)}
              />
              <Route
                path="knowledge-base/faqs"
                element={knowledgeRoute(<AdditionalFaqsSection />)}
              />
              <Route
                path="additional-faqs"
                element={<Navigate to={ADDITIONAL_FAQS_HREF} replace />}
              />
              <Route
                path="team"
                element={
                  showTeam ? (
                    <TeamSection />
                  ) : (
                    <Navigate to={`${STORE_INFO_BASE}/overview`} replace />
                  )
                }
              />
              <Route path="*" element={<IndexRedirect />} />
            </Routes>
          </div>
        </Surface>
      </Page>
    </>
  );
};

/** `/store-info` and legacy `/store-info?tab=hours` → the section route. */
const IndexRedirect: React.FC = () => {
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const section: Section = (tab && LEGACY_TAB_TO_SECTION[tab]) || 'overview';
  const next = new URLSearchParams(params);
  next.delete('tab');
  const qs = next.toString();
  return (
    <Navigate
      to={`${STORE_INFO_BASE}/${section}${qs ? `?${qs}` : ''}`}
      replace
    />
  );
};

export default StoreInfoPage;
